import { create }                     from 'zustand'
import { persist }                    from 'zustand/middleware'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  User,
} from 'firebase/auth'
import { doc, getDoc }               from 'firebase/firestore'

import { auth, db }                  from '@/lib/firebase'
import type { UserProfile, Workspace } from '@/types'

interface AuthState {
  user:          User | null
  profile:       UserProfile | null
  workspace:     Workspace | null
  loading:       boolean
  initialized:   boolean

  // Actions
  initAuth:      () => void
  login:         (email: string, password: string) => Promise<void>
  loginGoogle:   () => Promise<void>
  register:      (email: string, password: string, name: string) => Promise<void>
  logout:        () => Promise<void>
  refreshProfile: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user:        null,
      profile:     null,
      workspace:   null,
      loading:     true,
      initialized: false,

      // ── Inicializa listener do Firebase Auth ────────────
      initAuth: () => {
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            set({ user, loading: true })
            await get().refreshProfile()
          } else {
            set({ user: null, profile: null, workspace: null, loading: false })
          }
          set({ initialized: true })
        })
      },

      // ── Carrega perfil e workspace do Firestore ─────────
      refreshProfile: async () => {
        const user = auth.currentUser
        if (!user) return

        try {
          const profileRef = doc(db, 'users', user.uid)
          const profileSnap = await getDoc(profileRef)

          if (profileSnap.exists()) {
            const profile = profileSnap.data() as UserProfile

            // Carrega workspace (opcional)
        let workspace = null
        if (profile.workspaceId) {
          const wsRef = doc(db, 'workspaces', profile.workspaceId)
          const wsSnap = await getDoc(wsRef)
          workspace = wsSnap.exists()
            ? { id: wsSnap.id, ...wsSnap.data() } as Workspace
            : null
        }
        set({ profile, workspace, loading: false })
          } else {
            // Usuário existe no Auth mas não tem perfil → precisa completar onboarding
            set({ profile: null, workspace: null, loading: false })
          }
        } catch (error) {
          console.error('Erro ao carregar perfil:', error)
          set({ loading: false })
        }
      },

      // ── Login com e-mail e senha ─────────────────────────
      login: async (email, password) => {
        set({ loading: true })
        try {
          await signInWithEmailAndPassword(auth, email, password)
        } finally {
          set({ loading: false })
        }
      },

      // ── Login com Google ─────────────────────────────────
      loginGoogle: async () => {
        set({ loading: true })
        try {
          const provider = new GoogleAuthProvider()
          await signInWithPopup(auth, provider)
        } finally {
          set({ loading: false })
        }
      },

      // ── Registro ─────────────────────────────────────────
      register: async (email, password, _name) => {
        set({ loading: true })
        try {
          await createUserWithEmailAndPassword(auth, email, password)
          // O perfil é criado no Worker via /api/auth/register
        } finally {
          set({ loading: false })
        }
      },

      // ── Logout ───────────────────────────────────────────
      logout: async () => {
        await signOut(auth)
        set({ user: null, profile: null, workspace: null })
      },
    }),
    {
      name:    'dikis-auth',
      partialize: (state) => ({
        // Persiste apenas dados não sensíveis
        profile:   state.profile,
        workspace: state.workspace,
      }),
    },
  ),
)
